"use client"

import { ArrowRight } from "lucide-react"
import Image from "next/image"
import { useTranslations } from "next-intl"
import { useEffect, useState } from "react"

import landingBg from "@/../public/landing-bg.png"
import landingBgMobile from "@/../public/landing-bg-mobile.png"
import landingCustomer1 from "@/../public/landing-customer-1.jpg"
import landingMerchant1 from "@/../public/landing-merchant-1.jpg"
import SpectraFooter from "@/components/shared/SpectraFooter"
import { Button } from "@/components/ui/button"
import { Link } from "@/i18n/routing"
import { ROUTES } from "@/routes"
import { useIsMobile } from "@/hooks/useGlobalStore"

import SpectraExperience from "./_landing/SpectraExperience"

export default function LandingPage() {
  const isMobile = useIsMobile()
  const [isLandingLoaded, setIsLandingLoaded] = useState(false)
  const t = useTranslations("landing")

  useEffect(() => {
    setIsLandingLoaded(true)
  }, [])

  return (
    <>
      <main className="relative min-h-screen bg-cover bg-center pt-16">
        {isLandingLoaded && (
          <>
            <Image
              src={isMobile ? landingBgMobile : landingBg}
              alt="Landing page background"
              className="object-cover brightness-[0.9] md:brightness-100"
              onLoad={() => setIsLandingLoaded(true)}
              priority
              fill
            />
            <div className="absolute inset-0 bg-black/10 md:bg-black/50" />
            <div className="relative mx-auto flex min-h-[calc(100vh-4rem)] flex-col items-center justify-center  pb-32 pt-20">
              <div className="max-w-2xl  text-center">
                <h1 className="animate-fade-up text-4xl font-bold tracking-tight text-white opacity-0 [animation-delay:200ms] [animation-fill-mode:forwards] md:text-5xl">
                  {t.rich("hero.title", {
                    green: (chunks) => <span className="text-green-500">{chunks}</span>,
                  })}
                </h1>
                <h1 className="animate-fade-up text-4xl font-bold tracking-tight text-white opacity-0 [animation-delay:200ms] [animation-fill-mode:forwards] md:text-5xl">
                  {t.rich("hero.subtitle", {
                    green: (chunks) => <span className="text-green-500">{chunks}</span>,
                  })}
                </h1>

                <h2 className="mb-4 mt-6 animate-fade-up text-xl text-white opacity-0 [animation-delay:600ms] [animation-fill-mode:forwards] md:text-2xl">
                  {t.rich("hero.subtitle2", {
                    green: (chunks) => <span className="text-green-500">{chunks}</span>,
                  })}
                  <p>
                    {t.rich("hero.subtitle3", {
                      green: (chunks) => <span className="text-green-500">{chunks}</span>,
                    })}
                  </p>
                </h2>
                <div className="flex animate-fade-up justify-center opacity-0 [animation-delay:800ms] [animation-fill-mode:forwards]">
                  <div className="flex flex-col items-center gap-2">
                    <Link href={ROUTES.explore}>
                      <Button
                        size="lg"
                        className="group flex h-14 animate-fade-up items-center gap-2 bg-white px-10 text-lg font-medium text-black opacity-0 transition-all [animation-delay:1000ms] [animation-fill-mode:forwards] hover:bg-white/90"
                      >
                        {t("hero.exploreButton")}
                        <ArrowRight className="size-5 transition-transform group-hover:translate-x-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
                <Link href={ROUTES.signupCustomer}>
                  <div className="animate-fade-up font-semibold  opacity-0 [animation-delay:1000ms] [animation-fill-mode:forwards]">
                    <span className="mt-4 inline-block animate-scale-down  text-xl text-white underline underline-offset-4 hover:animate-scale-up">
                      {t("hero.joinButton")}
                    </span>
                  </div>
                </Link>
              </div>
            </div>
          </>
        )}
      </main>

      <section className="relative">
        <div className="container mx-auto px-4 py-16 md:py-0 md:pt-16">
          <div className="grid gap-12 md:grid-cols-2 lg:gap-16">
            {/* Left Column */}
            <div className="space-y-6">
              <div className="aspect-video overflow-hidden rounded-lg">
                <Image
                  src={landingCustomer1}
                  alt="Friends dining together"
                  className="size-full object-cover"
                  width={600}
                  height={400}
                  placeholder="blur"
                  priority
                />
              </div>
              <h2 className="text-3xl font-bold">{t("customer.title")}</h2>
              <p className="text-lg text-muted-foreground">{t("customer.description")}</p>
              <Link href={ROUTES.signupCustomer}>
                <Button variant="ghost" className="group pl-0 text-green-600 hover:bg-green-50 hover:text-green-700">
                  {t("customer.ctaButton")}
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <div className="aspect-video overflow-hidden rounded-lg">
                <Image
                  src={landingMerchant1}
                  alt="Chef preparing food"
                  className="size-full object-cover"
                  width={600}
                  height={400}
                  placeholder="blur"
                  priority
                />
              </div>
              <h2 className="text-3xl font-bold">{t("merchant.title")}</h2>
              <p className="text-lg text-muted-foreground">{t("merchant.description")}</p>
              <Link href={ROUTES.signupMerchant}>
                <Button variant="ghost" className="group pl-0 text-green-600 hover:bg-green-50 hover:text-green-700">
                  {t("merchant.ctaButton")}
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <SpectraExperience />
        <SpectraFooter />
      </section>
    </>
  )
}
